import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { marketplaceAPI } from '../../services/marketplaceAPI';
import { useCart } from '../../contexts/CartContext';
import DashboardButton from '../../components/DashboardButton';
import { getImageUrl } from '../../utils/imageUtils';

const ProductDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { addToCart } = useCart();
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [quantity, setQuantity] = useState(1);
  const [selectedLabOption, setSelectedLabOption] = useState(null);
  const [addingToCart, setAddingToCart] = useState(false);
  const [cartMessage, setCartMessage] = useState('');

  useEffect(() => {
    fetchProduct();
  }, [id]);

  const fetchProduct = async () => {
    try {
      setLoading(true);
      setError('');
      
      const response = await marketplaceAPI.getProduct(id);
      
      if (response.success) {
        setProduct(response.data);
        // Don't auto-select first lab option - let user choose
        setSelectedLabOption(null);
      } else {
        setError('Product not found');
      }
    } catch (err) {
      console.error('Error fetching product:', err);
      setError('Error loading product. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleAddToCart = async () => {
    // Validate lab selection for lab tests
    if (product.category === 'lab-test' && (!selectedLabOption || !product.labOptions || product.labOptions.length === 0)) {
      setCartMessage('Please select a lab option before adding to cart');
      return;
    }

    try {
      setAddingToCart(true);
      setCartMessage('');
      
      // Prepare lab option data for lab tests
      let labOptionData = null;
      if (product.category === 'lab-test' && selectedLabOption) {
        labOptionData = {
          labName: selectedLabOption.labName,
          price: selectedLabOption.price
        };
      }
      
      const result = await addToCart(product._id, quantity, labOptionData);
      
      if (result.success) {
        setCartMessage('Product added to cart successfully!');
        setTimeout(() => setCartMessage(''), 3000);
      } else {
        setCartMessage(result.message || 'Failed to add product to cart');
      }
    } catch (err) {
      console.error('Error adding to cart:', err);
      setCartMessage('Error adding product to cart. Please try again.');
    } finally {
      setAddingToCart(false);
    }
  };

  const handleQuantityChange = (change) => {
    const newQuantity = quantity + change;
    if (newQuantity >= 1 && newQuantity <= (product?.stock || 999)) {
      setQuantity(newQuantity);
    }
  };

  if (loading) {
    return (
      <div style={{
        minHeight: '100vh',
        background: 'linear-gradient(135deg, #2C2C2C 0%, #4A4A4A 50%, #6B6B6B 100%)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center'
      }}>
        <div style={{
          background: 'rgba(255, 255, 255, 0.1)',
          backdropFilter: 'blur(20px)',
          borderRadius: '1rem',
          padding: '2rem',
          display: 'flex',
          alignItems: 'center',
          gap: '1rem',
          color: 'white',
          fontSize: '1.2rem'
        }}>
          <div style={{
            width: '2rem',
            height: '2rem',
            border: '3px solid rgba(255, 255, 255, 0.3)',
            borderTop: '3px solid white',
            borderRadius: '50%',
            animation: 'spin 1s linear infinite'
          }}></div>
          Loading product details...
        </div>
      </div>
    );
  }

  if (error || !product) {
    return (
      <div style={{
        minHeight: '100vh',
        background: 'linear-gradient(135deg, #2C2C2C 0%, #4A4A4A 50%, #6B6B6B 100%)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center'
      }}>
        <div style={{
          background: 'rgba(255, 255, 255, 0.1)',
          backdropFilter: 'blur(20px)',
          borderRadius: '1rem',
          padding: '2rem',
          textAlign: 'center',
          color: 'white'
        }}>
          <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>⚠️</div>
          <div style={{ fontSize: '1.5rem', marginBottom: '1rem' }}>{error || 'Product not found'}</div>
          <button
            onClick={() => navigate('/marketplace')}
            style={{
              background: 'rgba(255, 255, 255, 0.2)',
              border: '1px solid rgba(255, 255, 255, 0.3)',
              color: 'white',
              padding: '0.75rem 1.5rem',
              borderRadius: '0.5rem',
              cursor: 'pointer',
              fontSize: '1rem',
              transition: 'all 0.3s ease'
            }}
            onMouseEnter={(e) => {
              e.target.style.background = 'rgba(255, 255, 255, 0.3)';
            }}
            onMouseLeave={(e) => {
              e.target.style.background = 'rgba(255, 255, 255, 0.2)';
            }}
          >
            Back to Marketplace
          </button>
        </div>
      </div>
    );
  }

  return (
    <div style={{
      minHeight: '100vh',
      background: '#F5F5F5'
    }}>
      {/* Dashboard and Cart Buttons */}
      <DashboardButton userRole="Patient" />

      {/* Header */}
      <div style={{
        background: 'linear-gradient(135deg, #2C2C2C 0%, #4A4A4A 50%, #6B6B6B 100%)',
        color: 'white',
        padding: '2rem 0',
        marginBottom: '2rem'
      }}>
        <div style={{
          maxWidth: '1200px',
          margin: '0 auto',
          padding: '0 2rem'
        }}>
          <button
            onClick={() => navigate(-1)}
            style={{
              background: 'rgba(255, 255, 255, 0.2)',
              border: '1px solid rgba(255, 255, 255, 0.3)',
              color: 'white',
              padding: '0.5rem 1rem',
              borderRadius: '0.5rem',
              cursor: 'pointer',
              fontSize: '0.9rem',
              marginBottom: '1rem',
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem',
              transition: 'all 0.3s ease'
            }}
            onMouseEnter={(e) => {
              e.target.style.background = 'rgba(255, 255, 255, 0.3)';
            }}
            onMouseLeave={(e) => {
              e.target.style.background = 'rgba(255, 255, 255, 0.2)';
            }}
          >
            ← Back
          </button>
          
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '1rem',
            marginBottom: '1rem'
          }}>
            <span style={{
              background: product.category === 'medicine' 
                ? 'rgba(16, 185, 129, 0.2)' 
                : 'rgba(59, 130, 246, 0.2)',
              border: '1px solid rgba(255, 255, 255, 0.3)',
              borderRadius: '2rem',
              padding: '0.5rem 1rem',
              fontSize: '0.9rem',
              fontWeight: '600'
            }}>
              {product.category === 'medicine' ? '💊 Medicine' : '🧪 Lab Test'}
            </span>
          </div>
          
          <h1 style={{
            fontSize: '2.5rem',
            fontWeight: '700',
            marginBottom: '0.5rem',
            textShadow: '0 2px 4px rgba(0,0,0,0.1)'
          }}>
            {product.name}
          </h1>
          
          <p style={{
            fontSize: '1.1rem',
            opacity: '0.9',
            maxWidth: '600px'
          }}>
            {product.description}
          </p>
        </div>
      </div>

      {/* Main Content */}
      <div style={{
        maxWidth: '1200px',
        margin: '0 auto',
        padding: '0 2rem',
        marginBottom: '2rem'
      }}>
        <div style={{
          display: 'grid',
          gridTemplateColumns: '1fr 1fr',
          gap: '3rem',
          alignItems: 'start'
        }}>
          {/* Product Image */}
          <div style={{
            background: '#FAFAFA',
            borderRadius: '1.5rem',
            padding: '2rem',
            boxShadow: '0 20px 40px rgba(0, 0, 0, 0.1)',
            position: 'sticky',
            top: '2rem',
            border: '1px solid #D3D3D3'
          }}>
            <img
              src={getImageUrl(product.image)}
              alt={product.name}
              style={{
                width: '100%',
                height: '400px',
                objectFit: 'cover',
                borderRadius: '1rem',
                boxShadow: '0 10px 30px rgba(0, 0, 0, 0.1)',
                border: '1px solid #D3D3D3'
              }}
              onError={(e) => {
                e.target.src = '/placeholder-product.png';
              }}
            />
          </div>

          {/* Product Details */}
          <div style={{
            background: '#FAFAFA',
            borderRadius: '1.5rem',
            padding: '2rem',
            boxShadow: '0 20px 40px rgba(0, 0, 0, 0.1)',
            border: '1px solid #D3D3D3'
          }}>
            {/* Price Section */}
            <div style={{
              marginBottom: '2rem',
              padding: '1.5rem',
              background: 'linear-gradient(135deg, #F5F5F5 0%, #EEEEEE 100%)',
              borderRadius: '1rem',
              border: '1px solid #D3D3D3'
            }}>
              <div style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                marginBottom: '1rem'
              }}>
                <span style={{
                  fontSize: '1.1rem',
                  fontWeight: '600',
                  color: '#6B6B6B'
                }}>
                  Price
                </span>
                <span style={{
                  fontSize: '2rem',
                  fontWeight: '700',
                  color: '#2C2C2C'
                }}>
                  ৳{selectedLabOption ? selectedLabOption.price : product.price}
                </span>
              </div>
              
              {product.category === 'medicine' && (
                <div style={{
                  fontSize: '0.9rem',
                  color: product.stock > 0 ? '#6B6B6B' : '#6B6B6B',
                  fontWeight: '500'
                }}>
                  {product.stock > 0 ? `${product.stock} items in stock` : 'Out of stock'}
                </div>
              )}
            </div>

            {/* Lab Options Section */}
            {product.category === 'lab-test' && product.labOptions && product.labOptions.length > 0 && (
              <div style={{ marginBottom: '2rem' }}>
                <h3 style={{
                  fontSize: '1.3rem',
                  fontWeight: '600',
                  color: '#2C2C2C',
                  marginBottom: '1rem'
                }}>
                  Select Lab Center
                </h3>
                
                <div style={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '0.75rem'
                }}>
                  {product.labOptions.map((option, index) => (
                    <label
                      key={index}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        padding: '1rem',
                        border: selectedLabOption && selectedLabOption.labName === option.labName
                          ? '2px solid #8B4513'
                          : '2px solid #D3D3D3',
                        borderRadius: '0.75rem',
                        cursor: 'pointer',
                        transition: 'all 0.3s ease',
                        background: selectedLabOption && selectedLabOption.labName === option.labName
                          ? 'linear-gradient(135deg, #F5F5F5 0%, #EEEEEE 100%)'
                          : '#FAFAFA'
                      }}
                      onMouseEnter={(e) => {
                        if (!selectedLabOption || selectedLabOption.labName !== option.labName) {
                          e.target.style.borderColor = '#8B4513';
                          e.target.style.background = '#F5F5F5';
                        }
                      }}
                      onMouseLeave={(e) => {
                        if (!selectedLabOption || selectedLabOption.labName !== option.labName) {
                          e.target.style.borderColor = '#D3D3D3';
                          e.target.style.background = '#FAFAFA';
                        }
                      }}
                    >
                      <input
                        type="radio"
                        name="labOption"
                        value={index}
                        checked={selectedLabOption && selectedLabOption.labName === option.labName}
                        onChange={() => setSelectedLabOption(option)}
                        style={{
                          marginRight: '1rem',
                          width: '1.2rem',
                          height: '1.2rem',
                          accentColor: '#8B4513'
                        }}
                      />
                      <div style={{ flex: 1 }}>
                        <div style={{
                          fontSize: '1rem',
                          fontWeight: '600',
                          color: '#2C2C2C',
                          marginBottom: '0.25rem'
                        }}>
                          {option.labName}
                        </div>
                        <div style={{
                          fontSize: '0.9rem',
                          color: '#6B6B6B'
                        }}>
                          Professional lab testing services
                        </div>
                      </div>
                      <div style={{
                        fontSize: '1.2rem',
                        fontWeight: '700',
                        color: '#2C2C2C'
                      }}>
                        ৳{option.price}
                      </div>
                    </label>
                  ))}
                </div>

                {selectedLabOption && (
                  <div style={{
                    marginTop: '1rem',
                    padding: '1rem',
                    background: 'linear-gradient(135deg, #F5F5F5 0%, #EEEEEE 100%)',
                    border: '1px solid #D3D3D3',
                    borderRadius: '0.75rem'
                  }}>
                    <div style={{
                      fontSize: '0.9rem',
                      fontWeight: '600',
                      color: '#2C2C2C',
                      marginBottom: '0.25rem'
                    }}>
                      ✅ Selected Lab Center
                    </div>
                    <div style={{
                      fontSize: '1rem',
                      fontWeight: '700',
                      color: '#2C2C2C'
                    }}>
                      {selectedLabOption.labName} - ৳{selectedLabOption.price}
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Product Information */}
            <div style={{ marginBottom: '2rem' }}>
              <h3 style={{
                fontSize: '1.3rem',
                fontWeight: '600',
                color: '#2C2C2C',
                marginBottom: '1rem'
              }}>
                Product Information
              </h3>
              
              <div style={{
                display: 'grid',
                gap: '1rem'
              }}>
                {product.category === 'medicine' && (
                  <>
                    {product.manufacturer && (
                      <div style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        padding: '0.75rem',
                        background: '#F5F5F5',
                        borderRadius: '0.5rem',
                        border: '1px solid #D3D3D3'
                      }}>
                        <span style={{ fontWeight: '500', color: '#6B6B6B' }}>Manufacturer</span>
                        <span style={{ fontWeight: '600', color: '#2C2C2C' }}>{product.manufacturer}</span>
                      </div>
                    )}
                    {product.dosage && (
                      <div style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        padding: '0.75rem',
                        background: '#F5F5F5',
                        borderRadius: '0.5rem',
                        border: '1px solid #D3D3D3'
                      }}>
                        <span style={{ fontWeight: '500', color: '#6B6B6B' }}>Dosage</span>
                        <span style={{ fontWeight: '600', color: '#2C2C2C' }}>{product.dosage}</span>
                      </div>
                    )}
                    {product.prescriptionRequired && (
                      <div style={{
                        padding: '0.75rem',
                        background: 'linear-gradient(135deg, #F5F5F5 0%, #EEEEEE 100%)',
                        border: '1px solid #D3D3D3',
                        borderRadius: '0.5rem',
                        color: '#2C2C2C',
                        fontWeight: '600'
                      }}>
                        ⚠️ Prescription Required
                      </div>
                    )}
                  </>
                )}

                {product.category === 'lab-test' && (
                  <>
                    {product.testType && (
                      <div style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        padding: '0.75rem',
                        background: '#F5F5F5',
                        borderRadius: '0.5rem',
                        border: '1px solid #D3D3D3'
                      }}>
                        <span style={{ fontWeight: '500', color: '#6B6B6B' }}>Test Type</span>
                        <span style={{ fontWeight: '600', color: '#2C2C2C' }}>{product.testType}</span>
                      </div>
                    )}
                    {product.sampleType && (
                      <div style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        padding: '0.75rem',
                        background: '#F5F5F5',
                        borderRadius: '0.5rem',
                        border: '1px solid #D3D3D3'
                      }}>
                        <span style={{ fontWeight: '500', color: '#6B6B6B' }}>Sample Type</span>
                        <span style={{ fontWeight: '600', color: '#2C2C2C' }}>{product.sampleType}</span>
                      </div>
                    )}
                    {product.reportDeliveryTime && (
                      <div style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        padding: '0.75rem',
                        background: '#F5F5F5',
                        borderRadius: '0.5rem',
                        border: '1px solid #D3D3D3'
                      }}>
                        <span style={{ fontWeight: '500', color: '#6B6B6B' }}>Report Delivery</span>
                        <span style={{ fontWeight: '600', color: '#2C2C2C' }}>{product.reportDeliveryTime}</span>
                      </div>
                    )}
                    {product.preparationInstructions && (
                      <div style={{
                        padding: '0.75rem',
                        background: 'linear-gradient(135deg, #F5F5F5 0%, #EEEEEE 100%)',
                        border: '1px solid #D3D3D3',
                        borderRadius: '0.5rem'
                      }}>
                        <div style={{
                          fontWeight: '600',
                          color: '#2C2C2C',
                          marginBottom: '0.5rem'
                        }}>
                          📋 Preparation Instructions
                        </div>
                        <div style={{
                          color: '#6B6B6B',
                          lineHeight: '1.5'
                        }}>
                          {product.preparationInstructions}
                        </div>
                      </div>
                    )}
                  </>
                )}
              </div>
            </div>

            {/* Add to Cart Section */}
            <div style={{
              borderTop: '1px solid #E5E5E5',
              paddingTop: '2rem'
            }}>
              {/* Quantity Selector */}
              <div style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                marginBottom: '1.5rem'
              }}>
                <span style={{
                  fontSize: '1rem',
                  fontWeight: '600',
                  color: '#2C2C2C'
                }}>
                  Quantity
                </span>
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  border: '2px solid #D3D3D3',
                  borderRadius: '0.75rem',
                  overflow: 'hidden'
                }}>
                  <button
                    type="button"
                    onClick={() => handleQuantityChange(-1)}
                    disabled={quantity <= 1}
                    style={{
                      padding: '0.75rem 1rem',
                      background: quantity <= 1 ? '#E5E5E5' : 'linear-gradient(135deg, #6B6B6B 0%, #4A4A4A 100%)',
                      color: quantity <= 1 ? '#6B6B6B' : 'white',
                      border: 'none',
                      cursor: quantity <= 1 ? 'not-allowed' : 'pointer',
                      fontSize: '1.2rem',
                      fontWeight: '600',
                      transition: 'all 0.3s ease'
                    }}
                    onMouseEnter={(e) => {
                      if (quantity > 1) {
                        e.target.style.background = 'linear-gradient(135deg, #4A4A4A 0%, #2C2C2C 100%)';
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (quantity > 1) {
                        e.target.style.background = 'linear-gradient(135deg, #6B6B6B 0%, #4A4A4A 100%)';
                      }
                    }}
                  >
                    -
                  </button>
                  <span style={{
                    padding: '0.75rem 1.5rem',
                    background: '#F5F5F5',
                    fontSize: '1.1rem',
                    fontWeight: '600',
                    color: '#2C2C2C',
                    minWidth: '3rem',
                    textAlign: 'center',
                    borderLeft: '1px solid #D3D3D3',
                    borderRight: '1px solid #D3D3D3'
                  }}>
                    {quantity}
                  </span>
                  <button
                    type="button"
                    onClick={() => handleQuantityChange(1)}
                    disabled={quantity >= (product.stock || 999)}
                    style={{
                      padding: '0.75rem 1rem',
                      background: quantity >= (product.stock || 999) ? '#E5E5E5' : 'linear-gradient(135deg, #6B6B6B 0%, #4A4A4A 100%)',
                      color: quantity >= (product.stock || 999) ? '#6B6B6B' : 'white',
                      border: 'none',
                      cursor: quantity >= (product.stock || 999) ? 'not-allowed' : 'pointer',
                      fontSize: '1.2rem',
                      fontWeight: '600',
                      transition: 'all 0.3s ease'
                    }}
                    onMouseEnter={(e) => {
                      if (quantity < (product.stock || 999)) {
                        e.target.style.background = 'linear-gradient(135deg, #4A4A4A 0%, #2C2C2C 100%)';
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (quantity < (product.stock || 999)) {
                        e.target.style.background = 'linear-gradient(135deg, #6B6B6B 0%, #4A4A4A 100%)';
                      }
                    }}
                  >
                    +
                  </button>
                </div>
              </div>

              {/* Add to Cart Button */}
              <button
                onClick={handleAddToCart}
                disabled={addingToCart || (product.category === 'lab-test' && !selectedLabOption)}
                style={{
                  width: '100%',
                  padding: '1rem 2rem',
                  background: addingToCart || (product.category === 'lab-test' && !selectedLabOption)
                    ? '#6B6B6B'
                    : 'linear-gradient(135deg, #8B4513 0%, #A0522D 100%)',
                  color: 'white',
                  border: 'none',
                  borderRadius: '0.75rem',
                  fontSize: '1.1rem',
                  fontWeight: '600',
                  cursor: addingToCart || (product.category === 'lab-test' && !selectedLabOption) ? 'not-allowed' : 'pointer',
                  transition: 'all 0.3s ease',
                  boxShadow: '0 4px 15px rgba(139, 69, 19, 0.3)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '0.5rem'
                }}
                onMouseEnter={(e) => {
                  if (!addingToCart && !(product.category === 'lab-test' && !selectedLabOption)) {
                    e.target.style.transform = 'translateY(-2px)';
                    e.target.style.boxShadow = '0 8px 25px rgba(139, 69, 19, 0.4)';
                  }
                }}
                onMouseLeave={(e) => {
                  if (!addingToCart && !(product.category === 'lab-test' && !selectedLabOption)) {
                    e.target.style.transform = 'translateY(0)';
                    e.target.style.boxShadow = '0 4px 15px rgba(139, 69, 19, 0.3)';
                  }
                }}
              >
                {addingToCart ? (
                  <>
                    <div style={{
                      width: '1rem',
                      height: '1rem',
                      border: '2px solid rgba(255, 255, 255, 0.3)',
                      borderTop: '2px solid white',
                      borderRadius: '50%',
                      animation: 'spin 1s linear infinite'
                    }}></div>
                    Adding to Cart...
                  </>
                ) : (
                  <>
                    🛒 Add to Cart
                    {selectedLabOption && (
                      <span style={{
                        fontSize: '0.9rem',
                        opacity: '0.9'
                      }}>
                        - ৳{(selectedLabOption.price * quantity).toFixed(2)}
                      </span>
                    )}
                  </>
                )}
              </button>

              {/* Cart Message */}
              {cartMessage && (
                <div style={{
                  marginTop: '1rem',
                  padding: '1rem',
                  borderRadius: '0.75rem',
                  fontSize: '0.9rem',
                  fontWeight: '500',
                  background: cartMessage.includes('successfully') 
                    ? 'linear-gradient(135deg, #F5F5F5 0%, #EEEEEE 100%)'
                    : 'linear-gradient(135deg, #F5F5F5 0%, #EEEEEE 100%)',
                  color: cartMessage.includes('successfully') ? '#2C2C2C' : '#6B6B6B',
                  border: cartMessage.includes('successfully')
                    ? '1px solid #D3D3D3'
                    : '1px solid #D3D3D3'
                }}>
                  {cartMessage}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* CSS for spinner animation */}
      <style>{`
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
};

export default ProductDetailPage;
